/**
 * Tipuri comune pentru exportul și importul de vault.
 *
 * Toate sursele de import (formatul propriu, Bitwarden, 1Password) se reduc la
 * `TransferCredential`; de acolo, un singur drum duce la credentialul criptat.
 */
import type { CredentialType, MetaValue } from '@/api/credentials';

export const CREDENTIAL_TYPES: readonly CredentialType[] = [
  'password',
  'ssh_key',
  'certificate',
  'api_key',
  'totp_seed',
  'db_connection',
  'secure_file',
  'generic',
];

/** Un credential în clar, așa cum stă în fișierul de export. */
export interface TransferCredential {
  type: CredentialType;
  title: string;
  url: string | null;
  username: string | null;
  expires_at: string | null;
  /** Atributele non-secrete (cele pe care serverul le vede oricum). */
  meta: Record<string, MetaValue>;
  /** Atributele secrete — conținutul decriptat al blob-ului. */
  payload: Record<string, unknown>;
}

/** Motivele stabile pentru care ceva nu poate fi citit sau importat. */
export type TransferErrorCode =
  | 'invalid_file'
  | 'unsupported_version'
  | 'wrong_password'
  | 'bitwarden_encrypted'
  | 'csv_malformed'
  | 'csv_no_known_columns'
  | 'onepux_no_data'
  | 'trashed'
  | 'unsupported_type'
  | 'empty_entry'
  | 'totp_unsupported'
  | 'missing_title'
  | 'missing_required'
  | 'invalid_number'
  | 'value_too_long'
  | 'too_large'
  | 'schema_missing'
  | 'duplicate';

/**
 * Eroare cu un cod stabil; textul afișat vine din i18n
 * (`transfer.errors.<code>`), ca logica pură să nu depindă de limbă.
 */
export class TransferError extends Error {
  constructor(
    public code: TransferErrorCode,
    public params: Record<string, string> = {},
  ) {
    super(code);
    this.name = 'TransferError';
  }
}

/** O intrare din fișierul sursă care nu a devenit credential. */
export interface SkippedEntry {
  title: string;
  code: TransferErrorCode;
  params?: Record<string, string>;
}

export interface ParsedImport {
  items: TransferCredential[];
  skipped: SkippedEntry[];
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Textul unei valori necunoscute: string-ul tăiat de spații sau ''. */
export function text(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}
